import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  LayoutDashboard,
  ClipboardList,
  UtensilsCrossed,
  Sliders,
  BarChart3,
  Bell,
  Settings,
  ChevronLeft,
  LogOut,
  MapPin,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../utils/cn';

const adminNavItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/commandes', icon: ClipboardList, label: 'Commandes' },
  { path: '/admin/menu', icon: UtensilsCrossed, label: 'Menu' },
  { path: '/admin/personnalisations', icon: Sliders, label: 'Personnalisations' },
  { path: '/admin/emplacements', icon: MapPin, label: 'Emplacements' },
  { path: '/admin/stats', icon: BarChart3, label: 'Statistiques' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin/parametres', icon: Settings, label: 'Paramètres' },
];

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Redirect if not owner
  if (user?.role !== 'owner') {
    navigate('/');
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-bg-darker">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-bg-darker border-b border-gray-800">
        <div className="pt-safe-top" />
        <div className="flex items-center justify-between max-w-6xl mx-auto px-4 h-16">
          <div className="flex items-center gap-3">
            {/* Burger Menu Button (Mobile) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-card transition-colors"
              aria-label="Ouvrir le menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            <button
              onClick={() => navigate('/')}
              className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-card transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Retour</span>
            </button>

            <div className="hidden md:block w-px h-8 bg-gray-700" />

            <div className="flex items-center gap-3">
              <div className="p-1 rounded-lg bg-white/30">
                <img
                  src="/logo-512.png"
                  alt="Ball M"
                  className="h-7 w-auto"
                />
              </div>
              <span className="text-lg font-bold text-text-primary">Admin</span>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-text-secondary hover:text-error hover:bg-error/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={cn(
          'fixed top-0 left-0 bottom-0 z-50 w-72 bg-bg-dark transform transition-transform duration-300 ease-in-out md:hidden',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="pt-safe-top" />

        {/* Menu Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-1 rounded-lg bg-white/30">
              <img src="/logo-512.png" alt="Ball M" className="h-8 w-auto" />
            </div>
            <div>
              <span className="text-lg font-bold text-text-primary">Ball M</span>
              <p className="text-xs text-text-muted">Administration</p>
            </div>
          </div>
          <button
            onClick={closeMobileMenu}
            className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-card transition-colors"
            aria-label="Fermer le menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {adminNavItems.map(({ path, icon: Icon, label, exact }) => {
            const isActive = exact
              ? location.pathname === path
              : location.pathname.startsWith(path) && path !== '/admin';

            return (
              <NavLink
                key={path}
                to={path}
                onClick={closeMobileMenu}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
                  isActive
                    ? 'bg-primary-orange text-white'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-card'
                )}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-bg-dark">
          <div className="p-3 bg-bg-card rounded-xl mb-3">
            <p className="text-xs text-text-muted">Connecté en tant que</p>
            <p className="text-sm font-medium text-text-primary truncate">{user?.first_name} {user?.last_name}</p>
          </div>

          <button
            onClick={() => {
              closeMobileMenu();
              navigate('/');
            }}
            className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-bg-card transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Retour au site</span>
          </button>
        </div>
      </div>

      {/* Sidebar (Desktop) */}
      <aside className="hidden md:block fixed left-0 top-16 bottom-0 w-64 bg-bg-dark border-r border-gray-800 p-4 pt-6">
        <nav className="space-y-1">
          {adminNavItems.map(({ path, icon: Icon, label, exact }) => {
            const isActive = exact
              ? location.pathname === path
              : location.pathname.startsWith(path) && path !== '/admin';

            return (
              <NavLink
                key={path}
                to={path}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary-orange/10 text-primary-orange'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-card'
                )}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Admin footer */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-3 bg-bg-card rounded-lg">
            <p className="text-xs text-text-muted">Connecté en tant que</p>
            <p className="text-sm font-medium text-text-primary truncate">{user?.first_name}</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-20 pb-8 md:pl-64 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
